import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import { useHistory } from 'react-router-dom';

import Tag from '../V2/Tag';
import Money from 'core/api/Money';
import { ReactComponent as FreezeSVG } from 'core/assets/images/V2/icons/FreezeIcon.svg';
import { PRODUCT_TEMP } from 'core/assets/images/V2';
import { Box, Button, Flex, Image, Radio } from 'core/ui/atoms';
import { productDescriptionPath } from 'onramp/api/navigation';

const MealItem = ({
  item,
  baseMealItem,
  isSelected,
  deliveryDayId,
  onSelectedMealChange,
}) => {
  const { dietTags, name, freezerFriendly, cost, images } = item;
  const history = useHistory();

  const RenderTags = () => {
    if (isEmpty(dietTags)) return null;
    return take(dietTags, 3)?.map((tag, index) => <Tag text={tag} key={index} />);
  };

  const RenderContainer = ({ children }) => {
    if (isSelected) {
      return <SelectedProductContainer>{children}</SelectedProductContainer>;
    } else {
      return <ProductContainer>{children}</ProductContainer>;
    }
  };

  const mealSize = item?.variants.find((variant) => variant.id === item.variantId)?.displayName;

  const getAmount = () => {
    const baseMealAmount = baseMealItem?.cost?.amount;
    const costAmount = cost?.amount;

    if (costAmount > baseMealAmount) {
      const differenceAmount = {
        __typename: 'Money',
        amount: costAmount - baseMealAmount,
      };
      return `(+${Money(differenceAmount).toFormat('$0,0')})`;
    } else if (baseMealAmount > costAmount) {
      const creditAmount = {
        __typename: 'Money',
        amount: baseMealAmount - costAmount,
      };
      return `(Credit ${Money(creditAmount).toFormat('$0,0')})`;
    }
  };

  const onHandleDetails = () => {
    history.push(productDescriptionPath(deliveryDayId, item?.id));
  };

  const imageURL = images?.[0]?.smallUrl || PRODUCT_TEMP;

  return (
    <RenderContainer>
      <ItemContainer direction="row" justifyContent="start">
        <SelectCont>
          <ProductImage src={imageURL} alt={name} />
          {freezerFriendly && (
            <FreezeBadge>
              <FreezeSVG />
            </FreezeBadge>
          )}
        </SelectCont>
        <DetailsContainer>
          <ProductName>{name}</ProductName>
          <Flex direction="row" gap="3px">
            <RenderTags />
          </Flex>
          <TagContainer>
            <Tag text={mealSize} style="outline" />
          </TagContainer>
        </DetailsContainer>
      </ItemContainer>
      <DetailSection direction="row" alignItems="center" justifyContent="space-between">
        <Radio
          name={item?.id}
          value="0"
          label={getAmount()}
          checked={isSelected}
          onChange={onSelectedMealChange}
        />
        <ViewDetailButton onClick={onHandleDetails}>View details</ViewDetailButton>
      </DetailSection>
    </RenderContainer>
  );
};

MealItem.propTypes = {
  item: PropTypes.object.isRequired,
  baseMealItem: PropTypes.object,
  onSelectedMealChange: PropTypes.func,
  isSelected: PropTypes.bool,
  deliveryDayId: PropTypes.string.isRequired,
};

const ItemContainer = styled(Flex)`
  width: 100%;
`;

const TagContainer = styled(Box)`
  margin-top: 8px;
`;

const ViewDetailButton = styled(Button)`
  width: 100px;
  font-size: 12px;
  padding: 0px;
  text-decoration: underline;
  text-underline-offset: 5px;
  text-transform: uppercase;
`;

const DetailSection = styled(Flex)`
  width: 95%;
  margin-top: 10px;
`;

const ProductImage = styled(Image)`
  height: 94px;
  width: 94px;
  object-fit: contain;
`;

const SelectCont = styled.div`
  position: relative;
  width: 28%;
  display: flex;
  justify-content: end;
  flex-shrink: 0;
  margin-right: 10px;
  &:last-child {
    margin-right: 0;
  }
`;

const FreezeBadge = styled(Box)`
  height: 20px;
  width: 45px;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  position: absolute;
  bottom: 0rem;
  left: 0rem;
`;

const SelectedProductContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 4px;
  background: rgba(197, 209, 192, 0.24);
`;

const ProductContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 4px;
`;

const DetailsContainer = styled(Box)`
  width: 67%;
`;

const ProductName = styled.p`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  line-height: 22px;
  margin: 0px;
  color: ${(p) => p.theme.colors.primaryV2};
`;

export default MealItem;
